import styled from '@emotion/styled';
import React from 'react';
import {SubMenuActionsType} from 'webapp-lib/Components';

import {Card} from '#';
import {Delete, Edit, Favorite, Play} from '_/assets/images';
import {showSimpleConfirmationDialog} from '_/sharedComponents/ConfirmationDialog';

import {DraggableWorkLogSuggestion} from '../../types';

export const CALENDAR_DRAGGABLE_CARD = 'cal-draggable-card';
export const CALENDAR_DRAGGABLE_CONTENT = 'cal-draggable-content';

interface BaseTemplateProps {
  data: DraggableWorkLogSuggestion;
  onStartTimer: () => void;
  children: React.ReactNode;
}

interface FavoritesTemplateProps extends BaseTemplateProps {
  onDelete: (item: DraggableWorkLogSuggestion) => void;
  onEdit: (item: DraggableWorkLogSuggestion) => void;
}

interface RecentTemplateProps extends BaseTemplateProps {
  onMakeFavorite: () => void;
}

interface TemplateProps extends BaseTemplateProps {
  extraActions?: SubMenuActionsType[];
  isForFavorites: boolean;
}

export const FavoriteTemplate = ({
  data,
  onStartTimer,
  onDelete,
  onEdit,
  children,
}: FavoritesTemplateProps): React.ReactElement => {
  const handleDelete = () => {
    showSimpleConfirmationDialog({
      headerText: 'Favorit löschen',
      message: 'Wollen sie die Favoritenvorlage wirklich löschen?',
      onConfirmed: () => onDelete(data),
    });
  };

  const handleEdit = () => onEdit(data);

  const extraActions = [
    {id: 'edit', Icon: Edit, callback: handleEdit, text: 'Editieren'},
    {id: 'delete', Icon: Delete, callback: handleDelete, text: 'Löschen'},
  ];

  return (
    <Template
      data={data}
      extraActions={extraActions}
      isForFavorites={true}
      onStartTimer={onStartTimer}
    >
      {children}
    </Template>
  );
};

export const RecentTemplate = ({
  data,
  onStartTimer,
  onMakeFavorite,
  children,
}: RecentTemplateProps): React.ReactElement => {
  const action: SubMenuActionsType[] = [
    {
      id: 'make-favorite',
      callback: onMakeFavorite,
      text: 'Zu Favoriten',
      Icon: Favorite,
    },
  ];

  return (
    <Template
      data={data}
      extraActions={action}
      isForFavorites={false}
      onStartTimer={onStartTimer}
    >
      {children}
    </Template>
  );
};

const Template = ({
  data,
  onStartTimer,
  extraActions,
  children,
  isForFavorites,
}: TemplateProps): React.ReactElement => {
  const actions = [
    {Icon: Play, callback: onStartTimer, text: 'Tracking starten'},
  ];

  return (
    <StyledFavoriteCard
      actions={actions}
      color={data.color}
      extraActions={extraActions}
      id={CALENDAR_DRAGGABLE_CARD}
      showBorderColor={isForFavorites}
      draggable
    >
      <div data-template={JSON.stringify(data)} id={CALENDAR_DRAGGABLE_CONTENT}>
        {children}
      </div>
    </StyledFavoriteCard>
  );
};

export const StyledFavoriteCard = styled(Card, {
  shouldForwardProp: (prop: string) =>
    !['color', 'showBorderColor'].includes(prop),
})<{
  color: string;
  showBorderColor: boolean;
}>`
  border-left: ${({color, showBorderColor}): string =>
    showBorderColor ? `${color} solid 12px !important` : 'none'};
  cursor: move;
`;
